<template>
  <View direction="column" gap="lg">
    <View direction="column" gap="xs">
      <Text variant="h2">General</Text>
      <Text variant="body-large" color="secondary">Your profile, contact details and communication preferences</Text>
    </View>

    <View align="center" gap="lg" class="avatar-row">
      <Avatar :user-name="user?.name || '?'" :image-url="user?.avatar" size="large" class="profile-avatar" />
      <View align="center" gap="sm">
        <Button variant="outline" size="medium" @click="photoModalRef?.show()">
          <Icon name="upload-cloud-02" size="small" />
          Upload photo
        </Button>
        <Button v-if="user?.avatar" variant="ghost" size="medium" :loading="isRemovingAvatar" @click="removeAvatar">
          Remove
        </Button>
      </View>
      <Text variant="body-small" color="tertiary">JPG or PNG, at least 240×240</Text>
    </View>

    <Divider />

    <div class="field-grid">
      <FormField label="Full Name" :error="form.fieldErrors('name')">
        <Input v-model="form.fields.name" placeholder="Enter your full name" />
      </FormField>

      <View direction="column" gap="sm">
        <View align="center" gap="sm">
          <Text variant="label-medium">Phone Number</Text>
          <Badge v-if="showPhoneVerifiedBadge" variant="green" size="small" icon="check-circle" label="Verified" />
          <Badge v-else-if="user?.phoneNumber" variant="yellow" size="small" label="Unverified" />
        </View>
        <FormField :error="form.fieldErrors('phoneNumber')">
          <div class="phone-field">
            <InputPhone v-model="form.fields.phoneNumber" />
            <Button
              v-if="showVerifyButton"
              class="verify-button"
              variant="secondary"
              size="small"
              :loading="isPreparingPhoneVerification"
              @click="startPhoneVerification"
            >
              Verify
            </Button>
          </div>
        </FormField>
      </View>

      <View direction="column" gap="sm" class="email-field">
        <View align="center" gap="sm">
          <Text variant="label-medium">Email</Text>
          <Badge v-if="pendingEmail" variant="yellow" size="small" label="Pending verification" />
          <Badge v-else variant="green" size="small" icon="check-circle" label="Verified" />
        </View>

        <FormField
          :error="form.fieldErrors('email')"
          :hint="
            pendingEmail
              ? undefined
              : 'Changing your email sends a verification link to the new address; it takes effect once confirmed.'
          "
        >
          <Input v-model="form.fields.email" type="email" :disabled="Boolean(pendingEmail)" />
        </FormField>

        <div v-if="pendingEmail" class="pending-banner">
          <Icon name="info-circle" size="small" class="pending-icon" />
          <Text variant="body-medium" as="div">
            We sent a link to
            <b>{{ pendingEmail }}</b>
            . Your email updates once it's confirmed.
            <button type="button" class="banner-action" :disabled="isResendingEmailLink" @click="resendEmailLink">
              Resend
            </button>
            ·
            <button type="button" class="banner-action" :disabled="isCancellingEmailChange" @click="cancelEmailChange">
              Cancel change
            </button>
          </Text>
        </div>
      </View>
    </div>

    <Divider />

    <View direction="column" gap="md">
      <View direction="column" gap="xs">
        <Text variant="h5">Preferred communication channel</Text>
        <Text variant="body-medium" color="secondary">Where we send notifications and updates</Text>
      </View>
      <View gap="sm" class="channel-cards">
        <RadioCard
          v-model="form.fields.preferredCommunicationChannel"
          name="preferredCommunicationChannel"
          :value="CommunicationChannel.EMAIL"
          class="channel-card"
        >
          <Text variant="label-medium">Email</Text>
        </RadioCard>
        <RadioCard
          v-model="form.fields.preferredCommunicationChannel"
          name="preferredCommunicationChannel"
          :value="CommunicationChannel.SMS"
          :disabled="smsChannelLocked"
          class="channel-card"
        >
          <Text variant="label-medium">SMS</Text>
        </RadioCard>
      </View>
      <Text v-if="smsChannelLocked" variant="body-small" color="tertiary">
        Texts need a verified phone number — verify yours above to enable SMS.
      </Text>
    </View>

    <Divider />

    <View justify="end">
      <Button :loading="form.submitting" @click="saveChanges">Save changes</Button>
    </View>
  </View>

  <ProfilePhotoModal ref="photoModalRef" />
  <VerifyPhoneModal ref="verifyPhoneModalRef" :phone-number="user?.phoneNumber || ''" />
  <ChangeEmailModal ref="changeEmailModalRef" :current-email="user?.email || ''" @requested="onEmailChangeRequested" />
</template>

<script setup lang="ts">
import { email as emailValidator, helpers, required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';

import {
  Avatar,
  Badge,
  Button,
  Divider,
  extractErrorMessage,
  FormField,
  fullNameValidator,
  Icon,
  Input,
  InputPhone,
  phoneNumberValidator,
  RadioCard,
  readableServerMessage,
  serverErrorCode,
  Text,
  useAlertsStore,
  useForm,
  View,
} from '@tallo/design-system';
import { CommunicationChannel } from '@tallo/investor/conversation';
import { phoneTakenErrorCode, profileService, useInvestorStore } from '@tallo/investor/investor';
import { normalizePhoneNumber } from '@tallo/utility';

import { useSeededForm } from '../composables/use-seeded-form';
import ChangeEmailModal from '../modals/change-email-modal.vue';
import ProfilePhotoModal from '../modals/profile-photo-modal.vue';
import VerifyPhoneModal from '../modals/verify-phone-modal.vue';

const investorStore = useInvestorStore();
const alertsStore = useAlertsStore();
const { investor } = storeToRefs(investorStore);

const user = computed(() => investor.value?.user);

const photoModalRef = ref<InstanceType<typeof ProfilePhotoModal>>();
const verifyPhoneModalRef = ref<InstanceType<typeof VerifyPhoneModal>>();
const changeEmailModalRef = ref<InstanceType<typeof ChangeEmailModal>>();

const isRemovingAvatar = ref(false);
const isPreparingPhoneVerification = ref(false);
const isResendingEmailLink = ref(false);
const isCancellingEmailChange = ref(false);

const form = useForm(
  {
    name: user.value?.name || '',
    email: user.value?.email || '',
    phoneNumber: user.value?.phoneNumber || '',
    preferredCommunicationChannel: user.value?.preferredCommunicationChannel || CommunicationChannel.EMAIL,
  },
  {
    name: {
      required: helpers.withMessage('Full name is required', required),
      fullNameValidator: helpers.withMessage(
        'Please enter your full name (first and last name with at least 2 characters each). Only letters, spaces, hyphens, apostrophes, and dots are allowed.',
        fullNameValidator,
      ),
    },
    email: {
      required: helpers.withMessage('Email is required', required),
      email: helpers.withMessage('Please enter a valid email address', emailValidator),
    },
    phoneNumber: {
      phoneNumberValidator,
    },
  },
);

const isNameDirty = computed(() => form.fields.name !== (user.value?.name || ''));
const isPhoneDirty = computed(
  () => normalizePhoneNumber(form.fields.phoneNumber) !== normalizePhoneNumber(user.value?.phoneNumber),
);
const isChannelDirty = computed(
  () => form.fields.preferredCommunicationChannel !== (user.value?.preferredCommunicationChannel || ''),
);

// SMS stays selectable for an account already on it (grandfathered numbers keep working);
// everyone else earns the option by verifying their phone first
const smsChannelLocked = computed(
  () => !user.value?.phoneVerifiedAt && form.fields.preferredCommunicationChannel !== CommunicationChannel.SMS,
);
const isEmailDirty = computed(
  () => !pendingEmail.value && form.fields.email.trim().toLowerCase() !== (user.value?.email || '').toLowerCase(),
);

const isDirty = computed(() => isNameDirty.value || isPhoneDirty.value || isChannelDirty.value || isEmailDirty.value);

const showPhoneVerifiedBadge = computed(
  () => Boolean(user.value?.phoneVerifiedAt) && Boolean(user.value?.phoneNumber) && !isPhoneDirty.value,
);
// The server's verdict on a number is about that number: it goes when the number changes
watch(
  () => form.fields.phoneNumber,
  () => form.clearValidationErrors(),
);

const showVerifyButton = computed(
  () => Boolean(form.fields.phoneNumber) && (!user.value?.phoneVerifiedAt || isPhoneDirty.value),
);

useSeededForm(user, reset, isDirty);

function reset() {
  form.fields.name = user.value?.name || '';
  form.fields.email = user.value?.email || '';
  form.fields.phoneNumber = user.value?.phoneNumber || '';
  form.fields.preferredCommunicationChannel = user.value?.preferredCommunicationChannel || CommunicationChannel.EMAIL;
  form.resetValidation();
}

function resetEmailField() {
  form.fields.email = user.value?.email || '';
}

// The pending change lives on its verification token server-side, so it's read from the
// email-change endpoint rather than the cached user
const pendingEmail = ref<string | null>(null);

onMounted(async () => {
  try {
    pendingEmail.value = (await profileService.getEmailChange()).pendingEmail;
  } catch {
    // The banner is a convenience; the tab stays usable without it
  }
});

function onEmailChangeRequested(newPendingEmail: string | null) {
  pendingEmail.value = newPendingEmail;
  resetEmailField();
}

function saveChanges() {
  form.submit(async () => {
    const emailChangeRequested = isEmailDirty.value;

    try {
      if (isNameDirty.value || isPhoneDirty.value || isChannelDirty.value) {
        await investorStore.updateUser({
          name: form.fields.name,
          phoneNumber: form.fields.phoneNumber || null,
          preferredCommunicationChannel: form.fields.preferredCommunicationChannel,
        });
      }

      if (emailChangeRequested) {
        changeEmailModalRef.value?.show(form.fields.email.trim());
      } else {
        alertsStore.success('Changes saved');
      }
    } catch (error) {
      reportSaveError(error, 'Failed to save changes');
    }
  });
}

// A number another account holds is the field's problem, not the page's: it stays under the input
// until the number changes, where a toast would have gone
function reportSaveError(error: unknown, fallback: string) {
  if (serverErrorCode(error) === phoneTakenErrorCode) {
    form.setFieldError('phoneNumber', readableServerMessage(error) ?? fallback);
    return;
  }

  alertsStore.error(extractErrorMessage(error, fallback));
}

async function removeAvatar() {
  isRemovingAvatar.value = true;

  try {
    await profileService.removeAvatar();
    await investorStore.load();
    alertsStore.success('Profile photo removed');
  } catch (error) {
    alertsStore.error(extractErrorMessage(error, 'Could not remove the photo'));
  } finally {
    isRemovingAvatar.value = false;
  }
}

// An edited number must be saved before it can receive the code
async function startPhoneVerification() {
  if (isPhoneDirty.value) {
    // fieldErrors is empty until the field is touched, so run validation before reading it
    form.validation.phoneNumber?.$touch?.();
    if (form.fieldErrors('phoneNumber').length) return;

    isPreparingPhoneVerification.value = true;
    try {
      await investorStore.updateUser({ phoneNumber: form.fields.phoneNumber || null });
    } catch (error) {
      reportSaveError(error, 'Could not save the phone number');
      return;
    } finally {
      isPreparingPhoneVerification.value = false;
    }
  }

  verifyPhoneModalRef.value?.show();
}

async function resendEmailLink() {
  isResendingEmailLink.value = true;

  try {
    await profileService.resendEmailChangeLink();
    alertsStore.success(`Verification link sent to <b>${pendingEmail.value}</b>`);
  } catch (error) {
    alertsStore.error(extractErrorMessage(error, 'Could not resend the link'));
  } finally {
    isResendingEmailLink.value = false;
  }
}

async function cancelEmailChange() {
  isCancellingEmailChange.value = true;

  try {
    await profileService.cancelEmailChange();
    pendingEmail.value = null;
    resetEmailField();
    alertsStore.success('Email change cancelled');
  } catch (error) {
    alertsStore.error(extractErrorMessage(error, 'Could not cancel the email change'));
  } finally {
    isCancellingEmailChange.value = false;
  }
}

defineExpose({ isDirty, reset });
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.profile-avatar {
  // The mock's 64px hero avatar; the component itself tops out at 3rem
  --avatar-size: 4rem !important;
  font-size: 1.25rem;
}

.avatar-row {
  flex-wrap: wrap;
}

.field-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--t-spacing-md);

  // Two columns only once the sidebar layout has room for them; below lg (stacked, or the narrower
  // sidebar range) it stays single-column so a collapsed rail never sits next to a 2-col grid.
  @include screen-min-lg {
    grid-template-columns: 1fr 1fr;
  }
}

.email-field {
  grid-column: 1 / -1;
}

.phone-field {
  position: relative;
  // Column flex so the InputPhone stretches to the cell width (a bare block lets it shrink to
  // its intrinsic size, leaving a gap before the Verify button in the single-column layout)
  display: flex;
  flex-direction: column;

  .verify-button {
    position: absolute;
    top: 50%;
    right: var(--t-spacing-sm);
    transform: translateY(-50%);
  }
}

.pending-banner {
  display: flex;
  gap: var(--t-spacing-md);
  align-items: flex-start;
  padding: var(--t-spacing-md);
  border-radius: var(--t-border-radius-md);
  background: var(--t-color-yellow-100);

  .pending-icon {
    flex-shrink: 0;
    color: var(--t-color-yellow-700);
    margin-top: 0.125rem;
  }
}

.banner-action {
  border: 0;
  padding: 0;
  background: none;
  font: inherit;
  font-weight: var(--t-font-weight-medium);
  cursor: pointer;

  &:disabled {
    color: var(--t-text-color-tertiary);
    cursor: default;
  }
}

.channel-cards {
  width: 100%;
}

.channel-card {
  flex: 1;
}
</style>
